import React, { useState, useEffect } from "react";
import {
  Button,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Input,
  Box,
  Heading,
  Select,
  useToast,
} from "@chakra-ui/react";
import { useUser } from "@clerk/clerk-react";
import {
  getAllVideos,
  deleteVideo,
  editVideoData,
} from "../../../../../services/adminService";
import Pagination from "../../../components/Pagination";
import videoCategories from "../../../../../data/videoCategories.json";

const VideoEditList = ({ clerkId }) => {
  const [videos, setVideos] = useState([]);
  const [editVideoId, setEditVideoId] = useState(null);
  const [editFormData, setEditFormData] = useState({
    title: "",
    description: "",
    url: "",
    category: "",
    display_order: 10,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentVideoPage, setCurrentVideoPage] = useState(1);
  const [videosPerPage] = useState(5);
  const [searchVideoTerm, setSearchVideoTerm] = useState("");
  const [filteredVideos, setFilteredVideos] = useState([]);
  const { user } = useUser();
  const toast = useToast();

  useEffect(() => {
    const fetchVideos = async () => {
      const data = await getAllVideos();
      setVideos(data || []);
    };

    fetchVideos();
  }, []);
  useEffect(() => {
    const filtered = videos.filter(
      (video) =>
        video.url?.toLowerCase().includes(searchVideoTerm.toLowerCase()) ||
        video.title?.toLowerCase().includes(searchVideoTerm.toLowerCase())
    );
    setFilteredVideos(filtered);
  }, [searchVideoTerm, videos]);

  const indexOfLastVideo = currentVideoPage * videosPerPage;
  const indexOfFirstVideo = indexOfLastVideo - videosPerPage;
  const currentVideos = filteredVideos.slice(
    indexOfFirstVideo,
    indexOfLastVideo
  );
  const totalVideoPages = Math.ceil(filteredVideos.length / videosPerPage);

  const handleEdit = (video) => {
    setEditVideoId(video.videoId);
    setEditFormData({
      title: video.title,
      description: video.description,
      url: video.url,
      category: video.category,
      display_order: video.display_order ?? 10,
    });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData({ ...editFormData, [name]: value });
  };

  const handleCancel = () => {
    setEditVideoId(null);
  };
  const handleSubmit = async () => {
    setIsSubmitting(true);
    await editVideoData(clerkId, { ...editFormData, videoId: editVideoId });
    setIsSubmitting(false);
    setEditVideoId(null);
    setVideos(
      videos.map((video) =>
        video.videoId === editVideoId ? { ...video, ...editFormData } : video
      )
    );
    toast({
      title: "Video edited",
      description: "Video edited successfully",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  const handleDelete = async (videoId, timestamp) => {
    await deleteVideo(user.id, videoId, timestamp);
    setVideos(videos.filter((video) => video.videoId !== videoId));
    toast({
      title: "Video deleted",
      description: "Video has been deleted successfully.",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  return (
    <Box>
      <Heading mt={4} mb={4}>
        Edit Videos
      </Heading>
      <Box mb={4}>
        <Input
          placeholder="Search by url or title"
          value={searchVideoTerm}
          onChange={(e) => setSearchVideoTerm(e.target.value)}
        />
      </Box>
      <Table>
        <Thead>
          <Tr>
            <Th>Title</Th>
            <Th>Description</Th>
            <Th>Url</Th>
            <Th>display</Th>
            <Th>Category</Th>
            <Th>Edit</Th>
            <Th>Delete</Th>
          </Tr>
        </Thead>
        <Tbody>
          {currentVideos.map((video) => (
            <Tr key={video.videoId}>
              <Td>{video.title}</Td>
              <Td>{video.description}</Td>
              <Td>{video.url}</Td>
              <Td>{video.display_order}</Td>
              <Td>{video.category}</Td>
              <Td>
                <Button onClick={() => handleEdit(video)} colorScheme="blue">
                  Edit
                </Button>
              </Td>
              <Td>
                <Button
                  colorScheme="red"
                  onClick={() => handleDelete(video.videoId, video.timestamp)}
                >
                  Delete
                </Button>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
      <Pagination
        currentPage={currentVideoPage}
        totalPages={totalVideoPages}
        onPageChange={setCurrentVideoPage}
      />
      {editVideoId && (
        <Box mt={4}>
          <Heading as="h3" size="md">
            Edit Video
          </Heading>
          <Input
            placeholder="Title"
            name="title"
            value={editFormData.title}
            onChange={handleFormChange}
            mb={2}
          />
          <Input
            placeholder="Description"
            name="description"
            value={editFormData.description}
            onChange={handleFormChange}
            mb={2}
          />
          <Input
            placeholder="URL"
            name="url"
            value={editFormData.url}
            onChange={handleFormChange}
            mb={2}
          />
          <Input
            type="number"
            name="display_order"
            placeholder="Display order"
            value={editFormData.display_order}
            onChange={(e) =>
              setEditFormData({
                ...editFormData,
                display_order: Number(e.target.value),
              })
            }
            mb={2}
          />
          <Select
            placeholder="Select category"
            name="category"
            value={editFormData.category}
            onChange={handleFormChange}
            mb={2}
            paddingRight="0.8rem"
          >
            {videoCategories.map((category, index) => (
              <option key={index} value={category}>
                {category}
              </option>
            ))}
          </Select>
          <Button
            mt={4}
            colorScheme="green"
            isLoading={isSubmitting}
            onClick={handleSubmit}
          >
            Update
          </Button>
          <Button mt={4} mr={4} colorScheme="blue" onClick={handleCancel}>
            Cancel
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default VideoEditList;
